export const COOKIE_CONSENT_STORAGE_KEY = 'hiromatch_cookie_consent_v1';

/** Garante-aligned maximum retention for stored consent (6 months). */
export const COOKIE_CONSENT_MAX_AGE_MS = 6 * 30 * 24 * 60 * 60 * 1000;

export const COOKIE_PREFERENCES_EVENT = 'hiromatch:open-cookie-preferences';

export interface CookieConsentPreferences {
  necessary: true;
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
  updatedAt: string;
}

/** Pre-choice defaults (Garante): only strictly necessary cookies are on. */
export const DEFAULT_COOKIE_CONSENT: CookieConsentPreferences = {
  necessary: true,
  functional: false,
  analytics: false,
  marketing: false,
  updatedAt: '',
};

function isConsentExpired(updatedAt: string): boolean {
  if (!updatedAt) return true;
  const ts = Date.parse(updatedAt);
  if (Number.isNaN(ts)) return true;
  return Date.now() - ts > COOKIE_CONSENT_MAX_AGE_MS;
}

export function readCookieConsent(): CookieConsentPreferences | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CookieConsentPreferences>;
    const updatedAt = typeof parsed.updatedAt === 'string' ? parsed.updatedAt : '';
    if (isConsentExpired(updatedAt)) {
      window.localStorage.removeItem(COOKIE_CONSENT_STORAGE_KEY);
      return null;
    }
    return {
      necessary: true,
      functional: Boolean(parsed.functional),
      analytics: Boolean(parsed.analytics),
      marketing: Boolean(parsed.marketing),
      updatedAt,
    };
  } catch {
    return null;
  }
}

export function writeCookieConsent(preferences: Omit<CookieConsentPreferences, 'necessary' | 'updatedAt'>) {
  if (typeof window === 'undefined') return;
  const value: CookieConsentPreferences = {
    necessary: true,
    functional: preferences.functional,
    analytics: preferences.analytics,
    // Marketing category not used yet — never persist as enabled (Task v2 / Option A).
    marketing: false,
    updatedAt: new Date().toISOString(),
  };
  window.localStorage.setItem(COOKIE_CONSENT_STORAGE_KEY, JSON.stringify(value));
  window.dispatchEvent(
    new CustomEvent('hiromatch:cookie-consent-updated', {
      detail: value,
    }),
  );
}